import { useCallback, useEffect, useRef } from 'react';

import { type APIRequest } from '../types';

type UseFetchInterceptorProps = {
  isOpen: boolean;
  shouldTrackUrl: (url: string) => boolean;
  onRequestCaptured: (request: APIRequest) => void;
  serializeHeaders: (headers?: HeadersInit) => Record<string, string> | undefined;
};

/**
 * Custom hook to intercept fetch requests and capture API call data for debugging.
 * Only active when the dev modal is open.
 */
export const useFetchInterceptor = ({
  isOpen,
  shouldTrackUrl,
  onRequestCaptured,
  serializeHeaders,
}: UseFetchInterceptorProps) => {
  const origFetchRef = useRef<typeof window.fetch | null>(null);

  const createRequestData = useCallback((body?: BodyInit): unknown => {
    try {
      if (!body) return undefined;
      if (typeof body === 'string') {
        try {
          return JSON.parse(body);
        } catch (_parseError) {
          return { type: 'String', value: body.slice(0, 100) + (body.length > 100 ? '...' : '') };
        }
      }
      if (body instanceof FormData) return { type: 'FormData' };
      if (body instanceof ArrayBuffer) return { type: 'ArrayBuffer', size: body.byteLength };
      if (body instanceof URLSearchParams) return { type: 'URLSearchParams' };
      if (body instanceof ReadableStream) return { type: 'ReadableStream' };
      if (body instanceof Blob) return { type: 'Blob', size: body.size };
      return { type: 'Unknown' };
    } catch (error) {
      console.warn('[DevModal] Error parsing request body:', error);
      return { error: 'Failed to parse request body' };
    }
  }, []);

  const parseResponseData = useCallback(async (response: Response): Promise<unknown> => {
    try {
      const clone = response.clone();
      const contentType = clone.headers.get('content-type') || '';

      if (contentType.includes('application/json')) {
        try {
          return await clone.json();
        } catch (jsonError) {
          console.warn('[DevModal] Failed to parse JSON response:', jsonError);
          return '[Invalid JSON response]';
        }
      }

      if (contentType.includes('text/')) {
        try {
          const text = await clone.text();
          return text.length > 1000 ? text.slice(0, 1000) + '...' : text;
        } catch (textError) {
          console.warn('[DevModal] Failed to parse text response:', textError);
          return '[Unable to parse text response]';
        }
      }

      return `[${contentType || 'Binary content'}]`;
    } catch (error) {
      console.warn('[DevModal] Error parsing response data:', error);
      return '[Unable to parse response]';
    }
  }, []);

  const createAPIRequest = useCallback(
    async (
      urlString: string,
      init: RequestInit | undefined,
      response: Response,
      duration: number,
    ): Promise<APIRequest> => {
      const requestData = createRequestData(init?.body || undefined);
      const responseData = await parseResponseData(response);
      const name = new URL(urlString).pathname.split('/').pop() || new URL(urlString).pathname;

      return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name,
        url: urlString,
        method: (init?.method || 'GET').toUpperCase(),
        status: response.status,
        timestamp: new Date(),
        duration,
        requestData,
        responseData,
        requestHeaders: serializeHeaders(init?.headers),
        responseHeaders: serializeHeaders(response.headers),
      };
    },
    [createRequestData, parseResponseData, serializeHeaders],
  );

  const createFailedRequest = useCallback(
    (urlString: string, init: RequestInit | undefined, duration: number, error?: unknown): APIRequest => {
      let name: string;
      try {
        name = new URL(urlString).pathname.split('/').pop() || new URL(urlString).pathname;
      } catch {
        name = urlString.split('/').pop() || urlString;
      }

      let errorMessage = '[Request failed]';
      if (error instanceof Error) {
        errorMessage = `[Request failed: ${error.message}]`;
      } else if (typeof error === 'string') {
        errorMessage = `[Request failed: ${error}]`;
      }

      return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name,
        url: urlString,
        method: (init?.method || 'GET').toUpperCase(),
        status: 0,
        timestamp: new Date(),
        duration,
        requestData: undefined,
        responseData: errorMessage,
        requestHeaders: serializeHeaders(init?.headers),
        responseHeaders: {},
      };
    },
    [serializeHeaders],
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !isOpen) return;

    // Save original fetch on first intercept
    if (!origFetchRef.current) {
      origFetchRef.current = window.fetch.bind(window);
    }

    const originalFetch = origFetchRef.current;

    // Create interceptor function
    const interceptedFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const start = performance.now();
      const urlString = typeof input === 'string' ? input : input.toString();

      try {
        const response = await originalFetch(input, init);
        const duration = Math.round(performance.now() - start);

        // Only track relevant URLs
        if (shouldTrackUrl(urlString)) {
          const apiRequest = await createAPIRequest(urlString, init, response, duration);
          onRequestCaptured(apiRequest);
        }

        return response;
      } catch (error) {
        const duration = Math.round(performance.now() - start);

        // Track failed requests too
        if (shouldTrackUrl(urlString)) {
          const failedRequest = createFailedRequest(urlString, init, duration, error);
          onRequestCaptured(failedRequest);
        }

        throw error;
      }
    };

    // Install interceptor
    window.fetch = interceptedFetch;

    // Cleanup: restore original fetch
    return () => {
      if (origFetchRef.current) {
        window.fetch = origFetchRef.current;
      }
    };
  }, [isOpen, shouldTrackUrl, onRequestCaptured, createAPIRequest, createFailedRequest]);

  // Provide cleanup function for manual restoration
  const restoreFetch = useCallback(() => {
    if (origFetchRef.current) {
      window.fetch = origFetchRef.current;
    }
  }, []);

  return { restoreFetch };
};
